//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import {
  ContentLibraryFilter,
  IContentLibraryAutocomplete,
  MediaFilterParamKey,
} from '@apis/content-library/contentLibraryApiModel';
import LoadingShimmerCaption from '@components/atoms/loading-shimmer/LoadingShimmerCaption';
import { BodyText, CaptionErrorText } from '@components/atoms/text/Text';
import { ClearSelectionButton } from '@components/media-filters/ClearSelectionButton';
import {
  FilterSection,
  FilterValue,
} from '@components/media-filters/filterSectionModels';
import { getSection } from '@components/media-filters/mediaFiltersUtils';
import SearchBar from '@components/molecules/search-bar/SearchBar';
import { ContentDrawerTrackingIds } from '@content-drawer/contentDrawerConstants';
import { EmptyLabels } from '@content-drawer/filters/EmptyLabels';
import { DEFAULT_HAS_LABELS_FILTER_SECTION } from '@content-drawer/filters/LabelsCheckbox';
import {
  CONTENT_DRAWER_LABELS_LIMIT,
  DEFAULT_LABELS_SECTION,
} from '@content-drawer/filters/LabelsFilter';
import { LabelsPanel } from '@content-drawer/filters/LabelsPanel';
import { AllLabelsDialog } from '@content-drawer/filters/labels/AllLabelsDialog';
import { findOptionByValue } from '@content-drawer/filters/labelsUtils';
import { isDefined } from '@utils/filterPredicates';

interface SelectLabelsSectionProps<T extends FilterValue> {
  error: Error | null;
  // TODO: Remove hideManageLabels once moderator label management is supported.
  hideManageLabels?: boolean;
  isDisabled?: boolean;
  isError: boolean;
  isFetching: boolean;
  labels: IContentLibraryAutocomplete[];
  moderatorLabelsQuery?: ContentLibraryFilter;
  onChange: (
    sections: Map<MediaFilterParamKey, FilterSection<FilterValue>>,
  ) => void;
  searchText: string;
  section: FilterSection<string> | undefined;
  sections: Map<MediaFilterParamKey, FilterSection<T>>;
  setSearchText: (text: string) => void;
  useModeratorLabels?: boolean;
}

export const SelectLabelsSection = <T extends FilterValue>({
  error,
  hideManageLabels,
  isDisabled,
  isError,
  isFetching,
  labels,
  moderatorLabelsQuery,
  onChange,
  searchText,
  section = DEFAULT_LABELS_SECTION,
  sections,
  setSearchText,
  useModeratorLabels,
}: SelectLabelsSectionProps<T>) => {
  const handleSelect = (selected: string) => {
    const options = [...section.options];
    const selectedOption = findOptionByValue(options, selected);
    const updatedSections = new Map<
      MediaFilterParamKey,
      FilterSection<FilterValue>
    >();

    if (selectedOption) {
      const updatedOptions = options.map((option) =>
        option.value === selectedOption.value
          ? { ...selectedOption, isSelected: !selectedOption.isSelected }
          : option,
      );

      updatedSections.set(section.paramKey, {
        ...section,
        options: updatedOptions,
      });
    } else {
      updatedSections.set(section.paramKey, {
        ...section,
        options: [
          ...options,
          {
            isSelected: true,
            label: selected,
            value: selected,
          },
        ],
      });
    }

    const parentSection =
      getSection(sections, 'hasLabels') ?? DEFAULT_HAS_LABELS_FILTER_SECTION;

    const parentOptions = parentSection.options.map((option) => ({
      ...option,
      isSelected: !!option.value,
    }));

    updatedSections.set('hasLabels', {
      ...parentSection,
      options: parentOptions,
    });

    onChange(updatedSections);
  };

  const isEmptyDataSet = !isFetching && !isError && labels.length === 0;
  const hasFullInitialLabelsSet =
    searchText.length > 0 ||
    (searchText === '' && labels.length >= CONTENT_DRAWER_LABELS_LIMIT);

  return (
    <LabelsSubsection>
      <SearchBar
        handleClear={() => setSearchText('')}
        isDisabled={(searchText === '' && labels.length === 0) || isDisabled}
        onChange={setSearchText}
        placeholder='Find labels...'
        trackingIdPrefix={ContentDrawerTrackingIds.SEARCH_LABELS}
        value={searchText}
        visuallyHiddenLabel='Find labels'
      />
      <Wrapper>
        <div>
          <ClearSelectionButton
            handleClearSection={(options) => {
              const updatedSections = new Map<
                MediaFilterParamKey,
                FilterSection<FilterValue>
              >();

              updatedSections.set(section.paramKey, { ...section, options });
              onChange(updatedSections);
            }}
            options={section.options}
          />
        </div>
        {isFetching ? (
          <LoadingCaption />
        ) : (
          (!isEmptyDataSet || searchText === '') && (
            <LabelsPanel
              isDisabled={isDisabled}
              labels={labels}
              onCheck={handleSelect}
              options={section.options}
              selectedOptions={section.options}
            />
          )
        )}
        {isEmptyDataSet && searchText !== '' && (
          <BodyText>No results found.</BodyText>
        )}
        {isEmptyDataSet && searchText === '' && <EmptyLabels />}
        {isDefined(error) && (
          <CaptionErrorText>{error?.message}</CaptionErrorText>
        )}
        <div>
          {hasFullInitialLabelsSet && (
            <AllLabelsDialog
              hideManageLabels={hideManageLabels}
              moderatorLabelsQuery={moderatorLabelsQuery}
              onChange={onChange}
              section={section}
              useModeratorLabels={useModeratorLabels}
            />
          )}
        </div>
      </Wrapper>
    </LabelsSubsection>
  );
};

const Wrapper = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[1]};
  `,
);

const LabelsSubsection = styled.div(
  ({ theme }) => `
    padding-left: ${theme.spacings[4]};
  `,
);

const LoadingCaption = styled(LoadingShimmerCaption)(
  ({ theme }) => `
    padding-top: ${theme.spacings[3]};
  `,
);
